import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal, type Signal } from '@angular/core';
import { catchError, map, of, type Observable } from 'rxjs';
import { type FeatureConfigDto } from '../models/feature-config-dto.model';
import { type FeatureConfig } from '../models/feature-config.model';
import { type FeatureFlags, featureFlagsArray } from '../models/feature-types';
import { EnvironmentService } from './environment.service';
import { FeatureFlagsRepository, createInitialConfig } from './feature-flags.repository';

@Injectable({
    providedIn: 'root',
})
export class FeatureFlagsService {
    private readonly environment = inject(EnvironmentService);
    private readonly http = inject(HttpClient);
    private readonly featureFlagsRepository = inject(FeatureFlagsRepository);
    private readonly config = signal<FeatureConfig | undefined>(undefined);

    public load(): Observable<FeatureConfig> {
        return this.http.get<FeatureConfigDto>(`${this.environment.baseHref}/api/feature-flags`, { params: { cb: Date.now() } }).pipe(
            map(data => {
                const config = this.combineConfigs(data);

                this.config.set(config);

                return config;
            }),
            catchError(() => {
                const config = this.combineConfigs();

                this.config.set(config);

                return of(config)
            }),
        );
    }

    public isFeatureEnabled(flag: FeatureFlags): Signal<boolean> {
        return computed(() => {
            const config = this.config();

            return !!config?.featureFlags && flag in config.featureFlags && config.featureFlags[flag];
        });
    }

    private combineConfigs(data?: FeatureConfigDto): FeatureConfig {
        const config = createInitialConfig(data);
        const configOverwrites = this.featureFlagsRepository.getValue();

        featureFlagsArray.forEach(featureFlag =>
            config.featureFlags[featureFlag] =
                (config.featureFlags[featureFlag] || configOverwrites.featureFlags[featureFlag]),
        );

        return config;
    }
}
